import { pb, createMassnahme, updateMassnahme, deleteMassnahme, type IstZustandMassnahme as ApiMassnahme } from '../lib/pocketbase';
import type { IstMassnahme } from '../components/AuditForm/types';

interface ApiFormData {
  audit?: string;
  istzustand?: string;
  massnahme?: string;
  bereich?: string;
  reihenfolge?: number;
  nummer?: number;
  erledigt?: boolean;
  bemerkung?: string;
}

export class MeasureService {
  private static transformToApiFormat(measure: Partial<IstMassnahme>): ApiFormData {
    const apiData: ApiFormData = {
      audit: measure.audit,
      istzustand: measure.istzustand,
      massnahme: measure.massnahme,
      bereich: measure.bereich,
      reihenfolge: measure.reihenfolge,
      nummer: measure.nummer,
      erledigt: measure.erledigt || false,
      bemerkung: measure.bemerkung || ''
    };

    return apiData;
  }

  private static transformFromApiFormat(record: ApiMassnahme): IstMassnahme {
    return {
      id: record.id,
      audit: record.audit,
      reihenfolge: record.reihenfolge,
      nummer: record.nummer,
      istzustand: record.istzustand,
      massnahme: record.massnahme,
      bereich: record.bereich || '',
      bemerkung: record.bemerkung || '',
      erledigt: record.erledigt,
      existingAnhaenge: Array.isArray(record.anhaenge) ? record.anhaenge : [],
      anhaenge: []
    };
  }

  private static async createFormData(apiData: ApiFormData, measure: Partial<IstMassnahme>): Promise<FormData> {
    const formData = new FormData();

    // Add basic fields
    Object.entries(apiData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    try {
      // Handle attachments
      // For new files
      if (Array.isArray(measure.anhaenge)) {
        measure.anhaenge.forEach(file => {
          if (file instanceof File) {
            if (!file.type.startsWith('image/')) {
              throw new Error('Nur Bilddateien sind erlaubt');
            }
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
              throw new Error('Dateien dürfen nicht größer als 5MB sein');
            }
            formData.append('anhaenge', file);
          }
        });
      }

      // For existing attachments
      if (Array.isArray(measure.existingAnhaenge)) {
        measure.existingAnhaenge.forEach(filename => {
          formData.append('anhaenge', filename);
        });
      }

      return formData;
    } catch (error: any) {
      throw new Error(`Fehler bei der Verarbeitung der Anhänge: ${error.message}`);
    }
  }

  static async getMeasuresForAudit(auditId: string): Promise<IstMassnahme[]> {
    try {
      const records = await pb.collection('ist_zustand_massnahmen').getFullList<ApiMassnahme>({
        filter: `audit = "${auditId}"`,
        sort: 'reihenfolge',
      });
      
      return records.map(record => this.transformFromApiFormat(record));
    } catch (error) {
      console.error('Error fetching measures:', error);
      throw new Error('Massnahmen konnten nicht geladen werden');
    }
  }

  static async getNextMeasureNumber(): Promise<number> {
    try {
      const records = await pb.collection('ist_zustand_massnahmen').getList(1, 1, {
        sort: '-nummer',
        fields: 'nummer'
      });
      
      if (records.items.length === 0) {
        return 1;
      }
      
      return (records.items[0].nummer || 0) + 1;
    } catch (error) {
      console.error('Error getting next measure number:', error);
      throw new Error('Nächste Massnahmen-Nummer konnte nicht ermittelt werden');
    }
  }

  static async createMeasure(measure: Partial<IstMassnahme>): Promise<ApiMassnahme> {
    try {
      // Validate required fields
      if (!measure.audit) throw new Error('Audit ID ist erforderlich');
      if (!measure.istzustand) throw new Error('Ist-Zustand ist erforderlich');
      if (!measure.massnahme) throw new Error('Massnahme ist erforderlich');
      if (!measure.bereich) throw new Error('Thema ist erforderlich');
      if (typeof measure.reihenfolge !== 'number') throw new Error('Reihenfolge ist erforderlich');

      const nummer = await this.getNextMeasureNumber();
      const apiData = this.transformToApiFormat({ ...measure, nummer });
      const formData = await this.createFormData(apiData, measure);

      return await createMassnahme(formData);
    } catch (error: any) {
      console.error('Error creating measure:', error);
      throw new Error(`Fehler beim Erstellen der Massnahme: ${error.message}`);
    }
  }

  static async updateMeasure(id: string, measure: Partial<IstMassnahme>): Promise<ApiMassnahme> {
    try {
      // Validate required fields
      if (!measure.audit) throw new Error('Audit ID ist erforderlich');
      if (!measure.istzustand) throw new Error('Ist-Zustand ist erforderlich');
      if (!measure.massnahme) throw new Error('Massnahme ist erforderlich');
      if (!measure.bereich) throw new Error('Thema ist erforderlich');
      if (typeof measure.reihenfolge !== 'number') throw new Error('Reihenfolge ist erforderlich');
      if (typeof measure.nummer !== 'number') throw new Error('Nummer ist erforderlich');

      // Transform to API format
      const apiData = this.transformToApiFormat(measure);
      const formData = await this.createFormData(apiData, measure);

      return await updateMassnahme(id, formData);
    } catch (error: any) {
      console.error('Error updating measure:', error);
      throw new Error(`Fehler beim Aktualisieren der Massnahme: ${error.message}`);
    }
  }

  static async deleteMeasure(id: string): Promise<void> {
    try {
      await deleteMassnahme(id);
    } catch (error: any) {
      console.error('Error deleting measure:', error);
      throw new Error('Massnahme konnte nicht gelöscht werden');
    }
  }

  static getFileUrl(measureId: string, filename: string): string {
    if (!measureId || !filename) {
      console.warn('Invalid parameters for getFileUrl:', { measureId, filename });
      return '';
    }
    return `${pb.baseUrl}/api/files/ist_zustand_massnahmen/${measureId}/${filename}`;
  }
}
